/**
 * TrackSidebar Component
 * Collapsible left sidebar with track list, add track buttons, and master volume
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { Mic, Piano, Drum, Volume2, X } from 'lucide-react';
import { InlineTrackControls } from './InlineTrackControls';

export const TrackSidebar = ({
  // Visibility
  isOpen,
  onClose,
  isMobile,
  
  // Tracks
  tracks,
  selectedTrack,
  setSelectedTrack,
  recordingTrack,
  isMidiRecording,
  
  // Track limits
  effectiveMaxTracks,
  subscriptionTrackLimit,
  maxTracks,
  
  // Track actions
  addTrack,
  addInstrumentTrack,
  updateTrack,
  removeTrack,
  duplicateTrack,
  
  // Recording
  isRecording,
  startRecording,
  stopRecording,
  startMidiRecording,
  stopMidiRecording,
  
  // Import
  handleFileImport,
  
  // Master volume
  masterVolume,
  setMasterVolume,
  
  // Feature limits
  getFeatureLimit,
  
  // Drag & drop
  draggedTrackId,
  dragOverTrackId,
  dropPosition,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  
  // Collaboration
  showCollaboration,
  collabUsers,
  currentUserId,
  onCollabTrackSelect,
  
  // Navigation
  navigate,
}) => {
  return (
    <AnimatePresence>
      {(isOpen || !isMobile) && (
        <motion.aside
          initial={isMobile ? { x: -256 } : false}
          animate={{ x: 0 }}
          exit={{ x: -256 }}
          className={`${isMobile ? 'absolute left-0 top-[57px] bottom-8 z-30' : 'relative'} w-64 bg-zinc-900/70 backdrop-blur-md border-r border-zinc-800/50 flex flex-col`}
          data-testid="track-sidebar"
        >
          {/* Add Track Buttons */}
          <div className="p-3 border-b border-zinc-800 space-y-2">
            {/* Track limit warning */}
            {tracks.length >= effectiveMaxTracks && subscriptionTrackLimit !== -1 && subscriptionTrackLimit < maxTracks && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-amber-400">Track limit reached</span>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="text-xs px-2 py-0.5 bg-amber-500 text-black rounded font-medium"
                  >
                    Upgrade
                  </button>
                </div>
              </div>
            )}
            
            {/* Audio Track Button */}
            <Button
              onClick={addTrack}
              disabled={tracks.length >= effectiveMaxTracks}
              className="w-full bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 text-indigo-300"
              data-testid="add-track"
            >
              <Mic className="w-4 h-4 mr-2" />
              Audio Track
            </Button>
            
            {/* Instrument Track Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => addInstrumentTrack('synth')}
                disabled={tracks.length >= effectiveMaxTracks || !getFeatureLimit?.('midi_tools')}
                variant="outline"
                size="sm"
                className="flex-1 border-purple-500/30 text-purple-300 hover:bg-purple-500/20 relative"
                data-testid="add-synth-track"
              >
                <Piano className="w-3 h-3 mr-1" />
                Synth
                {!getFeatureLimit?.('midi_tools') && (
                  <span className="absolute -top-1 -right-1 text-[8px] bg-amber-500 text-black px-1 rounded-full">PRO</span>
                )}
              </Button>
              <Button
                onClick={() => addInstrumentTrack('drums')}
                disabled={tracks.length >= effectiveMaxTracks || !getFeatureLimit?.('midi_tools')}
                variant="outline"
                size="sm"
                className="flex-1 border-amber-500/30 text-amber-300 hover:bg-amber-500/20 relative"
                data-testid="add-drums-track"
              >
                <Drum className="w-3 h-3 mr-1" />
                Drums
                {!getFeatureLimit?.('midi_tools') && (
                  <span className="absolute -top-1 -right-1 text-[8px] bg-amber-500 text-black px-1 rounded-full">PRO</span>
                )}
              </Button>
            </div>
            
            {/* Track count */}
            <div className="text-xs text-zinc-500 text-center flex items-center justify-center gap-1">
              <span>{tracks.length}/{effectiveMaxTracks} tracks</span>
              {subscriptionTrackLimit !== -1 && subscriptionTrackLimit < maxTracks && (
                <button 
                  onClick={() => navigate('/pricing')}
                  className="text-amber-400 hover:underline"
                >
                  (Get more)
                </button>
              )}
            </div>
          </div>
          
          {/* Master Volume */}
          <div className="p-3 border-b border-zinc-800">
            <div className="flex items-center gap-2 mb-2">
              <Volume2 className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-400">Master</span>
              <span className="ml-auto text-sm font-mono text-zinc-300">{Math.round(masterVolume * 100)}%</span>
            </div>
            <Slider
              value={[masterVolume * 100]}
              onValueChange={([v]) => setMasterVolume(v / 100)}
              max={100}
              step={1}
              className="w-full"
              data-testid="master-volume"
            />
          </div>
          
          {/* Track List */}
          <div className="flex-1 overflow-y-auto">
            {tracks.map((track, index) => (
              <InlineTrackControls
                key={track.id}
                track={track}
                index={index}
                isSelected={selectedTrack === track.id}
                isRecording={recordingTrack === track.id}
                isMidiRecording={isMidiRecording}
                onSelect={() => {
                  setSelectedTrack(track.id);
                  if (showCollaboration && onCollabTrackSelect) {
                    onCollabTrackSelect(track.id);
                  }
                }}
                onUpdate={(updates) => updateTrack(track.id, updates)}
                onRemove={() => removeTrack(track.id)}
                onDuplicate={() => duplicateTrack(track.id)}
                onRecord={() => isRecording ? stopRecording() : startRecording(track.id)}
                onMidiRecord={() => isMidiRecording ? stopMidiRecording() : startMidiRecording()}
                onImport={(file) => handleFileImport(track.id, file)}
                canRemove={tracks.length > 1}
                canDuplicate={tracks.length < effectiveMaxTracks}
                collabUsers={collabUsers}
                currentUserId={currentUserId}
                showCollaboration={showCollaboration}
                // Drag & drop
                isDragged={draggedTrackId === track.id}
                isDragOver={dragOverTrackId === track.id}
                dropPosition={dragOverTrackId === track.id ? dropPosition : null}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
              />
            ))}
          </div>
          
          {/* Mobile Close Button */}
          {isMobile && (
            <div className="p-3 border-t border-zinc-800">
              <Button
                variant="ghost"
                onClick={onClose}
                className="w-full text-zinc-400"
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default TrackSidebar;
